"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ObjectWithHistory = void 0;
/*
 * Copyright 2022, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
const cloneDeep_1 = __importDefault(require("lodash/cloneDeep"));
const isEqual_1 = __importDefault(require("lodash/isEqual"));
const project_config_1 = __importDefault(require("../../config/shared/project.config"));
const GenericObject_1 = require("./GenericObject");
/**
 * An object where change history is kept as fields are updated. This allows to
 * undo/redo operations. History is kept and the undo/redo functions are
 * available only if the object is in editing mode, ie after a call to the
 * `startEditing` method. Changes are automatically saved to history when done
 * using the `set` method. For changes done directly to the attributes or in any
 * other way, the `_updateHistory` protected method should be called directly.
 */
class ObjectWithHistory extends GenericObject_1.GenericObject {
    constructor(attributes, isNew = true) {
        super(attributes, isNew);
        this.history = [];
        this.historyIndex = -1;
        this.isEditing = false;
        this.updateHistoryCallback = undefined;
    }
    _innerSet(path, value) {
        super._innerSet(path, value);
        this._updateHistory();
    }
    _innerSetData(path, value) {
        super._innerSetData(path, value);
        this._updateHistory();
    }
    static isObjectWithHistory(obj) {
        return obj !== undefined && obj !== null && obj instanceof ObjectWithHistory;
    }
    canUndo() {
        return this.isEditing && this.historyIndex !== 0;
    }
    canRedo() {
        return this.isEditing && this.historyIndex !== this.history.length - 1;
    }
    undo() {
        if (this.isEditing && this.historyIndex > 0) {
            this._attributes = (0, cloneDeep_1.default)(this.history[--this.historyIndex]);
        }
    }
    redo() {
        if (this.isEditing && this.historyIndex + 1 < this.history.length) {
            this._attributes = (0, cloneDeep_1.default)(this.history[++this.historyIndex]);
        }
    }
    _updateHistory() {
        if (!this.isEditing) {
            return;
        }
        const undoCount = project_config_1.default.undoCount || 10;
        if (this.historyIndex + 1 < this.history.length) {
            this.history.splice(this.historyIndex + 1, this.history.length);
        }
        this.history.push((0, cloneDeep_1.default)(this._attributes));
        this.historyIndex++;
        while (this.history.length > undoCount) {
            this.history.shift();
            this.historyIndex--;
        }
        if (typeof this.updateHistoryCallback === 'function') {
            this.updateHistoryCallback();
        }
    }
    hasChanged(attribute) {
        if (!this.isEditing) {
            return false;
        }
        if (attribute === undefined) {
            return this.isNew() || (!this.isNew() && !(0, isEqual_1.default)(this._attributes, this.history[0]));
        }
        else {
            return this.history.length > 1 && !(0, isEqual_1.default)(this._attributes[attribute], this.history[0][attribute]);
        }
    }
    startEditing() {
        //this.updateHistory();
        this.history = [(0, cloneDeep_1.default)(this._attributes)];
        this.historyIndex = 0;
        this.isEditing = true;
    }
    stopEditing() {
        this.history = [];
        this.historyIndex = -1;
        this.isEditing = false;
    }
    cancelEditing() {
        if (!this.isEditing) {
            return;
        }
        this._attributes = (0, cloneDeep_1.default)(this.history[0]);
        this.historyIndex = -1;
        this.isEditing = false;
    }
}
exports.ObjectWithHistory = ObjectWithHistory;
//# sourceMappingURL=ObjectWithHistory.js.map